<template>
    <Transition name="sidebar-transition">
        <div
            v-show="headerStore.sidebarOpen"
            class="sidebar sidebar--mobile"
            :class="{ 'sidebar--open': headerStore.sidebarOpen }"
        >
            <div class="sidebar__header">
                Menu
                <nav class="sidebar__nav-primary" aria-label="sidebar-primary">
                    <ul>
                        <li>
                            <a :href="portalUrl" class="main-header__logo">
                                <img src="/shared-img/header/sazka-logo.svg" alt="Logo Sazky" width="28" height="28" />
                            </a>
                        </li>
                        <li>
                            <a :href="`${portalUrl}/loterie`">Loterie</a>
                        </li>
                        <li>
                            <a :href="`${portalUrl}/losy`">Losy</a>
                        </li>
                        <li>
                            <a :href="`${portalUrl}/hry`">Hry</a>
                        </li>
                        <li>
                            <a :href="`${portalUrl}/hry-live`">Hry Live</a>
                        </li>
                    </ul>
                </nav>
            </div>
            <div class="sidebar__content">
                <SidebarUser />
                <ul class="sidebar__links">
                    <li v-if="isLoggedIn">
                        <button @click="clickMyAccount">
                            <SvgIcon name="account" />
                            Můj účet
                        </button>
                    </li>
                    <li v-if="priceBoostId">
                        <RouterLink
                            @click="sendAnalytics('Navýšené kurzy', 0)"
                            :to="`${PATHS.PREMATCH}/${priceBoostId}`"
                        >
                            <img
                                src="/shared-img/mobile-menu-component/navyseni-kurzu.png"
                                alt="Ikona navýšených kurzů"
                                width="20"
                                height="20"
                            />
                            Navýšené kurzy
                        </RouterLink>
                    </li>
                    <li v-for="(item, index) in items" :key="index">
                        <DynamicLink @click="sendAnalytics(item.name, index + 1)" :path="item.linkUrl" :blank="item.blank" :is-external="item.isExternal">
                            <img v-if="item.image?.url" :src="item.image.url" :alt="item.name" width="20" height="20" />
                            <SvgIcon v-else :name="item.svg" />
                            {{ item.name }}
                        </DynamicLink>
                    </li>
                    <li class="sidebar__darkmode">
                        <SvgIcon name="darkmode" />
                        Tmavý režim
                        <DarkmodeSwitch />
                    </li>
                </ul>
                <div class="sidebar__section">
                    <OngoingContestsSidebar :contests />
                </div>
                <div class="sidebar__section">
                    <div class="sidebar__section__title">Ostatní</div>
                    <Accordion>
                        <AccordionTab
                            v-if="menu?.information?.length"
                            disable-analytics
                            @click="sendAnalytics('Informace', items.length + contests.length + 2)"
                            :open="false"
                        >
                            <template #headline> Informace </template>
                            <DynamicLink v-for="(link, index) in menu.information" :key="index" :path="link.linkUrl" :blank="link.blank" :is-external="link.isExternal">
                                {{ link.name }}
                            </DynamicLink>
                        </AccordionTab>
                        <AccordionTab
                            disable-analytics
                            v-for="(footerItem, index) in footerItems.slice(1)"
                            :key="`${index}-${footerItem.name}`"
                            :open="false"
                            @click="sendAnalytics(footerItem?.name, index + items.length + contests.length + 3)"
                        >
                            <template #headline> {{ footerItem.name }} </template>
                            <DynamicLink
                                :path="footerItemLink.linkUrl"
                                v-for="(footerItemLink, footerItemLinkIndex) in footerItem.items"
                                :key="`${index}-${footerItemLinkIndex}-${footerItemLink.name}`"
                                :blank="footerItemLink.blank"
                                :is-external="footerItemLink.isExternal"
                            >
                                {{ footerItemLink.name }}
                            </DynamicLink>
                        </AccordionTab>
                    </Accordion>
                </div>
            </div>
            <div class="sidebar__footer">
                <button @click="useChat().show" class="sidebar__footer__chat-button">
                    <SvgIcon name="chat-sb" />Kontaktovat podporu
                </button>
                <div class="sidebar__footer__disclaimer">
                    <template v-if="isLoggedIn">
                        <HeaderLoggedDuration> Čas přihlášení: </HeaderLoggedDuration> |
                    </template>
                    Hraj s rozumem 18+. Účast na hazardní hře může být škodlivá.
                </div>
            </div>
        </div>
    </Transition>
    <Transition name="sidebar-backdrop-transition">
        <div
            class="sidebar--mobile-backdrop"
            v-if="headerStore.sidebarOpen"
            @click="headerStore.sidebarOpen = false"
        ></div>
    </Transition>
</template>

<script lang="ts" setup>
import { onMounted } from "vue";
import { storeToRefs } from "pinia";
import HeaderLoggedDuration from "@/header/components/atoms/HeaderLoggedDuration.vue";
import SidebarUser from "@/header/components/molecules/SidebarUser.vue";
import DynamicLink from "@/shared/components/atoms/DynamicLink.vue";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";
import DarkmodeSwitch from "@/header/components/atoms/DarkmodeSwitch.vue";
import Accordion from "@/shared/components/organisms/Accordion.vue";
import AccordionTab from "@/shared/components/molecules/AccordionTab.vue";
import OngoingContestsSidebar from "@/header/components/molecules/OngoingContestsSidebar.vue";
import { useNgSession } from "@/shared/ng/ng-session";
import { ngFrameworkLoaded } from "@/shared/ng/ng-integration";
import analytics from "@/shared/analytics";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { useHeader } from "@/shared/stores/use-header";
import useNavigation from "@/shared/composables/use-navigation";
import useSazkaContests, { contests } from "@/shared/composables/use-sazka-contests";
import { MenuItem } from "@/api/interfaces/menu";
import { PATHS } from "@/router/paths";
import { useChat } from "@/shared/composables/use-chat";

defineProps<{
    items: MenuItem[];
    footerItems: MenuItem[];
}>();

const { isLoggedIn } = useNgSession();
const headerStore = useHeader();
const drilldownNodeStore = useDrilldownNodesStore();
const { priceBoostId } = storeToRefs(drilldownNodeStore);
const { getContests } = useSazkaContests();
const { menu } = useNavigation();

const portalUrl = environment.portalBaseUrl;

const clickMyAccount = async () => {
    headerStore.sidebarOpen = false;

    try {
        await ngFrameworkLoaded();

        ng_fw.showMyAccount();
    } catch (error) {
        logException("sidebar", error, { message: "An error has occurred while opening My Account" });
    }
};

const sendAnalytics = (name: string, index: number) => {
    analytics.sendSideMenuClick(name, index);
};

onMounted(async () => {
    if (contests.value?.length) return;

    await getContests();
});
</script>
